/**
 * Typed fetch client for the Jarvis backend.
 *
 * Every function here maps 1:1 to an endpoint documented in CONTRACTS.md and
 * returns the exact model from `src/types.ts`. Builders: READ ONLY — add new
 * calls additively at the bottom, never change an existing signature.
 *
 * Dev: Vite proxies /api -> http://127.0.0.1:${JARVIS_PORT}. No base URL needed.
 */

import type {
  Account,
  AccountCreate,
  AccountDeleteResult,
  AccountPatch,
  AccountsOverview,
  BudgetItemUpsert,
  CategoriesResponse,
  CryptoCatalog,
  CurrencyList,
  DebtList,
  DebtUpsert,
  FinanceOverview,
  FinanceSyncStatus,
  ReconcileResult,
  Transaction,
  TransactionChange,
  TransactionCreate,
  ReimburseRequest,
  TransactionPage,
  TransactionPatch,
  TransferRequest,
  WellbeingCreateRequest,
  WellbeingEntry,
  WellnessAnalysis,
  WellnessOverview,
  ExerciseHistory,
  TrainingPlan,
  TrainingPlanDay,
  TrainingWeek,
  WellnessCalendar,
  WellnessRecommendations,
  CalendarDay,
  ProductSummary,
  BusinessMoney,
  ApiError,
  AuthProviders,
  AuthUser,
  ChecklistItem,
  ConnectorName,
  ConnectorItem,
  ConnectorSettings,
  ConnectorTokenPayload,
  IngestTokenCreated,
  IngestTokenState,
  SessionList,
  DashboardSummary,
  GoalCreate,
  GoalNode,
  GoalPatch,
  GoalPeriods,
  GoalsOverview,
  HealthReport,
  ProgressEntry,
  StepPatch,
  TaskItem,
  TasksToday,
  TaskUpdate,
  JournalDay,
  JournalLockStatus,
  JournalMonth,
  JournalWin,
  JournalWinCreate,
  LoginPayload,
  MetricSeriesResponse,
  Note,
  Notebook,
  NotebookCreate,
  NotebookDetail,
  NotebookPage,
  NotebookPageSave,
  NotebookPatch,
  NoteResurface,
  NotesFeed,
  NoteStatusChange,
  NoteToGoal,
  NoteToTask,
  NoteView,
  OkResponse,
  PasswordChangePayload,
  SpendingReport,
  SubscriptionsResponse,
  SubscriptionUpsert,
} from "../types";
import { REFRESH_HEADER, refreshPending } from "./refresh";

const API_BASE = "/api";

/** Error thrown by every helper in this module when the backend does not return 2xx. */
export class JarvisApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, detail: string) {
    super(detail);
    this.name = "JarvisApiError";
    this.status = status;
    this.code = code;
  }
}

type QueryValue = string | number | boolean | undefined | null;

type UnauthenticatedHandler = () => void;

let unauthenticatedHandler: UnauthenticatedHandler | null = null;

/**
 * Registered once, by `AuthProvider` (`hooks/useAuth.tsx`). Every `request()`
 * failure with `code: "unauthenticated"` outside `/api/auth/*` calls it instead
 * of leaving each page to notice its own 401 — the session ended somewhere
 * else (another tab, the TTL, a revoked cookie) and every open screen has to
 * agree at once, by dropping into the login screen, not by each retrying on
 * its own.
 */
export function setUnauthenticatedHandler(handler: UnauthenticatedHandler | null): void {
  unauthenticatedHandler = handler;
}

function buildUrl(path: string, params?: Record<string, QueryValue>): string {
  const url = `${API_BASE}${path}`;
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

async function request<T>(
  path: string,
  init?: RequestInit & { params?: Record<string, QueryValue> },
): Promise<T> {
  const { params, ...rest } = init ?? {};
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((rest.headers as Record<string, string> | undefined) ?? {}),
  };
  // «Оновити» was pressed a moment ago: this read goes past the server's cache
  // (lib/refresh.ts). Reads only — a write has nothing cached to skip.
  if ((rest.method ?? "GET").toUpperCase() === "GET" && refreshPending()) {
    headers[REFRESH_HEADER] = "1";
  }
  const response = await fetch(buildUrl(path, params), { ...rest, headers });

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    let code = "http_error";
    try {
      const body = (await response.json()) as Partial<ApiError>;
      if (body.detail) detail = body.detail;
      if (body.code) code = body.code;
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    // `/api/auth/*` reports its own 401s (bad password, no session yet) as
    // ordinary form errors — only every OTHER endpoint's 401 means "the
    // session that was live has ended".
    if (response.status === 401 && code === "unauthenticated" && !path.startsWith("/auth/")) {
      unauthenticatedHandler?.();
    }
    throw new JarvisApiError(response.status, code, detail);
  }

  // 204 and other empty bodies: json() would throw a SyntaxError, so endpoints
  // that only report success would be unusable through this client.
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/* ------------------------------------------------------------------ calls */

/* ---------------------------------------------------------------- auth */

/** POST /api/auth/login — e-mail + password; sets the HttpOnly session cookie. */
export const login = (body: LoginPayload) =>
  request<AuthUser>("/auth/login", { method: "POST", body: JSON.stringify(body) });

/** POST /api/auth/logout — idempotent; ends whatever session cookie is there. */
export const logout = () => request<OkResponse>("/auth/logout", { method: "POST" });

/** GET /api/auth/me — the session probe. 401 code:"unauthenticated" with none. */
export const getMe = () => request<AuthUser>("/auth/me");

/** GET /api/auth/providers — which login methods are configured server-side. */
export const getAuthProviders = () => request<AuthProviders>("/auth/providers");

/** POST /api/auth/password — self-service change; the current password must verify. */
export const changePassword = (body: PasswordChangePayload) =>
  request<OkResponse>("/auth/password", { method: "POST", body: JSON.stringify(body) });

/* ------------------------------------------------------------ connectors */

/** GET /api/settings/connectors — status of every connector, never the token value. */
export const getConnectorSettings = () => request<ConnectorSettings>("/settings/connectors");

/** PUT /api/settings/connectors/{name} — save this user's own token(s). */
export const putConnectorToken = (name: ConnectorName, payload: ConnectorTokenPayload) =>
  request<ConnectorItem>(`/settings/connectors/${name}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

/** POST /api/settings/ingest-token — mint this account's device token; shown once. */
export const createIngestToken = () =>
  request<IngestTokenCreated>("/settings/ingest-token", { method: "POST" });

/** GET /api/settings/sessions — every device signed into this account. */
export const getSessions = () => request<SessionList>("/settings/sessions");

/** POST /api/settings/sessions/revoke — sign one device out. */
export const revokeDevice = (userAgent: string) =>
  request<SessionList>("/settings/sessions/revoke", {
    method: "POST",
    body: JSON.stringify({ user_agent: userAgent }),
  });

/** POST /api/settings/sessions/revoke-others — sign out every other device. */
export const revokeOtherSessions = () =>
  request<SessionList>("/settings/sessions/revoke-others", { method: "POST" });

/** PATCH /api/settings/profile — rename the account. */
export const saveProfileName = (name: string) =>
  request<{ name: string }>("/settings/profile", {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });

/** PUT /api/journal/lock-minutes — how long one unlock lasts here. */
export const setJournalLockMinutes = (minutes: number) =>
  request<JournalLockStatus>("/journal/lock-minutes", {
    method: "PUT",
    body: JSON.stringify({ minutes }),
  });

/** PUT /api/journal/pin — change it, proving the current one. */
export const changeJournalPin = (current_pin: string, new_pin: string) =>
  request<JournalLockStatus>("/journal/pin", {
    method: "PUT",
    body: JSON.stringify({ current_pin, new_pin }),
  });

/** DELETE /api/settings/ingest-token — devices stop being able to push. */
export const deleteIngestToken = () =>
  request<IngestTokenState>("/settings/ingest-token", { method: "DELETE" });

/** DELETE /api/settings/connectors/{name} — drop this user's own token(s). */
export const deleteConnectorToken = (name: ConnectorName) =>
  request<ConnectorItem>(`/settings/connectors/${name}`, { method: "DELETE" });

/** GET /api/summary — everything the home screen needs, in one request. */
export const getSummary = () => request<DashboardSummary>("/summary");

/** GET /api/finance/spending?from&to — dates are ISO YYYY-MM-DD, inclusive. */
export const getSpending = (from?: string, to?: string) =>
  request<SpendingReport>("/finance/spending", { params: { from, to } });

/** POST /api/finance/sync — start pulling fresh statements straight from Monobank. */
export const startFinanceSync = () =>
  request<FinanceSyncStatus>("/finance/sync", { method: "POST" });

/** GET /api/finance/sync — progress of the running (or last finished) pull. */
export const getFinanceSyncStatus = () => request<FinanceSyncStatus>("/finance/sync");

/** GET /api/finance/subscriptions — from personal_tracker. */
export const getSubscriptions = () => request<SubscriptionsResponse>("/finance/subscriptions");

/** GET /api/business/product?business&from&to — a bot/app business. */
export const getProductSummary = (business = "lovytalon", from?: string, to?: string) =>
  request<ProductSummary>("/business/product", { params: { business, from, to } });

/** GET /api/business/money — the business's income, costs, profit; what the ФОП owes. */
export const getBusinessMoney = (from: string, to: string) =>
  request<BusinessMoney>("/business/money", { params: { from, to } });

/** GET /api/goals — goals grouped by level + habits with streaks. */
export const getGoals = (params?: { year?: string; month?: string; week?: string }) =>
  request<GoalsOverview>("/goals", { params });

/** GET /api/goals/periods — period keys the switcher can offer, newest first. */
export const getGoalPeriods = () => request<GoalPeriods>("/goals/periods");

/** POST /api/goals — `progress_type` picks how it is ticked off; omit to derive. */
export const createGoal = (body: GoalCreate) =>
  request<GoalNode>("/goals", { method: "POST", body: JSON.stringify(body) });

/** PATCH /api/goals/{id} — omitted fields are left alone. */
export const patchGoal = (id: string, body: GoalPatch) =>
  request<GoalNode>(`/goals/${id}`, { method: "PATCH", body: JSON.stringify(body) });

/** DELETE /api/goals/{id} — takes its steps and progress history with it. */
export const deleteGoal = (id: string) => request<void>(`/goals/${id}`, { method: "DELETE" });

/** POST /api/goals/{id}/steps — turns a yes/no goal into a checklist. */
export const addGoalStep = (goalId: string, text: string) =>
  request<ChecklistItem>(`/goals/${goalId}/steps`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });

/** PATCH /api/goals/steps/{id} — tick, rename or reorder one step. */
export const patchGoalStep = (stepId: string, body: StepPatch) =>
  request<void>(`/goals/steps/${stepId}`, { method: "PATCH", body: JSON.stringify(body) });

/** DELETE /api/goals/steps/{id}. */
export const deleteGoalStep = (stepId: string) =>
  request<void>(`/goals/steps/${stepId}`, { method: "DELETE" });

/** POST /api/goals/{id}/progress — records a day and sets the current value. */
export const logGoalProgress = (goalId: string, body: ProgressEntry) =>
  request<GoalNode>(`/goals/${goalId}/progress`, { method: "POST", body: JSON.stringify(body) });

/** GET /api/tasks/today — overdue first, then everything due today. */
export const getTasksToday = () => request<TasksToday>("/tasks/today");

/** POST /api/tasks — lands in the Todoist Inbox with today's date. */
export const createTask = (content: string) =>
  request<TaskItem>("/tasks", { method: "POST", body: JSON.stringify({ content }) });

/** PATCH /api/tasks/{id} — rename a task, move it to another day, or both. */
export const updateTask = (id: string, patch: TaskUpdate) =>
  request<TaskItem>(`/tasks/${id}`, { method: "PATCH", body: JSON.stringify(patch) });

/** POST /api/tasks/{id}/close. */
export const closeTask = (id: string) => request<void>(`/tasks/${id}/close`, { method: "POST" });

/** POST /api/tasks/{id}/reopen. */
export const reopenTask = (id: string) => request<void>(`/tasks/${id}/reopen`, { method: "POST" });

/** DELETE /api/tasks/{id} — irreversible in Todoist, so the UI confirms first. */
export const deleteTask = (id: string) => request<void>(`/tasks/${id}`, { method: "DELETE" });

/** GET /api/wellness/overview?days — training, body metrics, wellbeing. */
export const getWellness = (days = 30) =>
  request<WellnessOverview>("/wellness/overview", { params: { days } });

/** POST /api/wellness/checkin — upsert today's mood/energy. */
export const saveWellbeing = (body: WellbeingCreateRequest) =>
  request<WellbeingEntry>("/wellness/checkin", { method: "POST", body: JSON.stringify(body) });

/** GET /api/health — service + data-source health. */
export const getHealth = () => request<HealthReport>("/health");

/** GET /api/metrics?metric&days&dim — a metrics_daily series. */
export const getMetrics = (metric: string, params?: { days?: number; dim?: string }) =>
  request<MetricSeriesResponse>("/metrics", { params: { metric, ...params } });

/** GET /api/wellness/analysis?weeks — the deep-analysis tab in one call (6|12|24). */
export const getWellnessAnalysis = (weeks: number) =>
  request<WellnessAnalysis>("/wellness/analysis", { params: { weeks } });

/** GET /api/wellness/training/exercise/{id}?days — one exercise's progress. */
export const getExerciseHistory = (templateId: string, days = 365) =>
  request<ExerciseHistory>(`/wellness/training/exercise/${encodeURIComponent(templateId)}`, {
    params: { days },
  });

/** GET /api/wellness/week?anchor — plan vs fact for the week containing `anchor`. */
export const getTrainingWeek = (anchor?: string) =>
  request<TrainingWeek>("/wellness/week", { params: { anchor } });

/** GET /api/wellness/calendar?month — dense YYYY-MM month of check-ins + workouts. */
export const getWellnessCalendar = (month?: string) =>
  request<WellnessCalendar>("/wellness/calendar", { params: { month } });

/** PUT /api/wellness/plan — replace the whole weekly plan; [] clears it. */
export const saveTrainingPlan = (days: TrainingPlanDay[]) =>
  request<TrainingPlan>("/wellness/plan", { method: "PUT", body: JSON.stringify({ days }) });

/** GET /api/wellness/recommendations — «що покращити» + per-muscle balance. */
export const getRecommendations = () =>
  request<WellnessRecommendations>("/wellness/recommendations");

/* ------------------------------------------------------------- journaling */

/*
 * The six content calls below need a live unlock and answer 401
 * `journal_locked` without one. The unlock is an HttpOnly cookie the server
 * sets on `unlockJournal`/`setJournalPin` — same-origin, so `fetch` carries it
 * by default and nothing here has to (or can) read it.
 */

/** GET /api/journal/lock — is a PIN set, and is this browser open? No content. */
export const getJournalLock = () => request<JournalLockStatus>("/journal/lock");

/** POST /api/journal/unlock — trade the PIN for an unlock cookie. */
export const unlockJournal = (pin: string) =>
  request<JournalLockStatus>("/journal/unlock", {
    method: "POST",
    body: JSON.stringify({ pin }),
  });

/** POST /api/journal/pin — set this account's first PIN and open the tab. */
export const setJournalPin = (pin: string) =>
  request<JournalLockStatus>("/journal/pin", {
    method: "POST",
    body: JSON.stringify({ pin }),
  });

/** POST /api/journal/lock — re-lock now. Idempotent. */
export const lockJournal = () => request<JournalLockStatus>("/journal/lock", { method: "POST" });

/** POST /api/journal/touch — "he is still here": restart the idle window. */
export const touchJournal = () => request<JournalLockStatus>("/journal/touch", { method: "POST" });

/** GET /api/journal/month?year&month — dense day meta, month stats, streaks. */
export const getJournalMonth = (year: number, month: number) =>
  request<JournalMonth>("/journal/month", { params: { year, month } });

/** GET /api/journal/day/{day} — entry + wins; an unwritten day comes back empty. */
export const getJournalDay = (day: string) =>
  request<JournalDay>(`/journal/day/${encodeURIComponent(day)}`);

/** PUT /api/journal/day/{day} — empty or whitespace-only `content_md` deletes the entry. */
export const putJournalEntry = (day: string, content_md: string) =>
  request<JournalDay>(`/journal/day/${encodeURIComponent(day)}`, {
    method: "PUT",
    body: JSON.stringify({ content_md }),
  });

/** POST /api/journal/wins — `day` omitted means today; the server stamps the time. */
export const createJournalWin = (payload: JournalWinCreate) =>
  request<JournalWin>("/journal/wins", { method: "POST", body: JSON.stringify(payload) });

/** PATCH /api/journal/wins/{id} — rewrite one win's text. */
export const updateJournalWin = (id: number, text: string) =>
  request<JournalWin>(`/journal/wins/${id}`, { method: "PATCH", body: JSON.stringify({ text }) });

/** DELETE /api/journal/wins/{id}. */
export const deleteJournalWin = (id: number) =>
  request<void>(`/journal/wins/${id}`, { method: "DELETE" });

/* ------------------------------------------------------- ledger (stage 2) */

/** Filters accepted by `GET /api/finance/transactions`. */
export interface TransactionQuery {
  from?: string;
  to?: string;
  account?: string;
  category?: string;
  kind?: "expense" | "income" | "transfer";
  needs_review?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

/** GET /api/finance/transactions — one filtered page of the ledger. */
export const getTransactions = (query: TransactionQuery = {}) =>
  request<TransactionPage>("/finance/transactions", { params: { ...query } });

/** POST /api/finance/transactions — a hand-entered expense, income or transfer. */
export const createTransaction = (body: TransactionCreate) =>
  request<Transaction>("/finance/transactions", { method: "POST", body: JSON.stringify(body) });

/** PATCH /api/finance/transactions/{id} — category, note or exclusion. */
export const patchTransaction = (id: string, body: TransactionPatch) =>
  request<TransactionChange>(`/finance/transactions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

/** DELETE /api/finance/transactions/{id} — hand-entered rows only. */
export const deleteTransaction = (id: string) =>
  request<void>(`/finance/transactions/${id}`, { method: "DELETE" });

/** POST /api/finance/transactions/{id}/transfer — name the other side, or leave it pending. */
export const markTransfer = (id: string, body: TransferRequest) =>
  request<Transaction>(`/finance/transactions/${id}/transfer`, {
    method: "POST",
    body: JSON.stringify(body),
  });

/** DELETE /api/finance/transactions/{id}/transfer — back to expense/income. */
export const unmarkTransfer = (id: string) =>
  request<Transaction>(`/finance/transactions/${id}/transfer`, { method: "DELETE" });

/** GET /api/finance/accounts/all — every live account, not only Monobank's. */
export const getAllAccounts = () => request<AccountsOverview>("/finance/accounts/all");

/** POST /api/finance/accounts — an account no bank reports on. */
export const createAccount = (body: AccountCreate) =>
  request<Account>("/finance/accounts", { method: "POST", body: JSON.stringify(body) });

/** PATCH /api/finance/accounts/{id}. */
export const patchAccount = (id: string, body: AccountPatch) =>
  request<Account>(`/finance/accounts/${id}`, { method: "PATCH", body: JSON.stringify(body) });

/** DELETE /api/finance/accounts/{id} — deletes when it can, archives when it cannot. */
export const deleteAccount = (id: string) =>
  request<AccountDeleteResult>(`/finance/accounts/${id}`, { method: "DELETE" });

/** POST /api/finance/subscriptions — a new recurring charge. */
export const createSubscription = (body: SubscriptionUpsert) =>
  request<SubscriptionsResponse>("/finance/subscriptions", {
    method: "POST",
    body: JSON.stringify(body),
  });

/** PATCH /api/finance/subscriptions/{id} — edit one, or pause it with active:false. */
export const patchSubscription = (id: string, body: SubscriptionUpsert) =>
  request<SubscriptionsResponse>(`/finance/subscriptions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

/** DELETE /api/finance/subscriptions/{id}. */
export const deleteSubscription = (id: string) =>
  request<SubscriptionsResponse>(`/finance/subscriptions/${id}`, { method: "DELETE" });

/** POST /api/finance/accounts/{id}/reconcile — set it to what is really in there. */
export const reconcileAccount = (id: string, actualBalance: number) =>
  request<ReconcileResult>(`/finance/accounts/${id}/reconcile`, {
    method: "POST",
    body: JSON.stringify({ actual_balance: actualBalance }),
  });

/** GET /api/finance/categories — groups, categories and rules in one call. */
export const getCategories = () => request<CategoriesResponse>("/finance/categories");

/* ------------------------------------------ budgets, overview */

/** GET /api/finance/overview — wealth, spendable, budget. */
export const getFinanceOverview = () => request<FinanceOverview>("/finance/overview");

/** POST /api/finance/budget/items — set one limit; 0 removes the line. */
export const setBudgetItem = (body: BudgetItemUpsert) =>
  request<FinanceOverview>("/finance/budget/items", {
    method: "POST",
    body: JSON.stringify(body),
  });

/** POST /api/finance/budget/confirm — turn this month's draft into the plan. */
export const confirmBudget = () =>
  request<FinanceOverview>("/finance/budget/confirm", { method: "POST" });

/** POST /api/finance/budget/proposal — fill the empty lines from history. */
export const applyBudgetProposal = () =>
  request<FinanceOverview>("/finance/budget/proposal", { method: "POST" });

/** GET /api/finance/debts — the list on its own; the only view of settled ones. */
export const getDebts = (includeSettled = false) =>
  request<DebtList>(`/finance/debts?include_settled=${includeSettled}`);

/** GET /api/finance/currencies — what an amount may be denominated in, with today's rates. */
export const getCurrencies = () => request<CurrencyList>("/finance/currencies");

/** POST /api/finance/debts — record who owes whom. Null id creates, an id edits. */
export const upsertDebt = (body: DebtUpsert) =>
  request<FinanceOverview>("/finance/debts", { method: "POST", body: JSON.stringify(body) });

/** POST /api/finance/debts/{id}/settle — repaid; leaves the open list, stays in the table. */
export const settleDebt = (id: string) =>
  request<FinanceOverview>(`/finance/debts/${id}/settle`, { method: "POST" });

/** DELETE /api/finance/debts/{id} — recorded by mistake. Repaid debts get settled instead. */
export const deleteDebt = (id: string) =>
  request<FinanceOverview>(`/finance/debts/${id}`, { method: "DELETE" });

/** GET /api/finance/crypto/coins — the picker list. */
export const getCryptoCoins = () => request<CryptoCatalog>("/finance/crypto/coins");

/**
 * GET /api/notes — the stream, narrowed by search, tags and status. All compose.
 *
 * `tag` is a comma-separated list read as OR: a note carrying any of them.
 * `view` is a slice, not a status: 'active' also carries the deferred notes
 * whose date has come round.
 */
export const getNotes = (params?: {
  query?: string;
  tag?: string;
  archived?: boolean;
  view?: NoteView | "";
}) => request<NotesFeed>("/notes", { params });

/** POST /api/notes — the text and the tags it was captured under. */
export const createNote = (content_md: string, tags: string[] = []) =>
  request<Note>("/notes", { method: "POST", body: JSON.stringify({ content_md, tags }) });

/** PATCH /api/notes/{id} — omit `tags` to save the text and keep the chips. */
export const updateNote = (id: string, content_md: string, tags?: string[]) =>
  request<Note>(`/notes/${id}`, {
    method: "PATCH",
    body: JSON.stringify(tags ? { content_md, tags } : { content_md }),
  });

/** DELETE /api/notes/{id} — for good. Archiving is the reversible way to hide one. */
export const deleteNote = (id: string) => request<void>(`/notes/${id}`, { method: "DELETE" });

/** POST /api/notes/{id}/archive — stays readable and restorable. */
export const archiveNote = (id: string) =>
  request<Note>(`/notes/${id}/archive`, { method: "POST" });

/** POST /api/notes/{id}/restore — also counts as confirming the note is live. */
export const restoreNote = (id: string) =>
  request<Note>(`/notes/${id}/restore`, { method: "POST" });

/**
 * POST /api/notes/{id}/status — what became of the idea.
 *
 * Separate from archiving: a note can be done and still in the stream, or
 * archived and still active. «Завтра»/«наступний тиждень» go as presets so the
 * date is counted in Kyiv days, not in the browser's timezone.
 */
export const setNoteStatus = (id: string, body: NoteStatusChange) =>
  request<Note>(`/notes/${id}/status`, { method: "POST", body: JSON.stringify(body) });

/** POST /api/notes/{id}/reviewed — «ще актуально»; drops out of the card for a month. */
export const markNoteReviewed = (id: string) =>
  request<Note>(`/notes/${id}/reviewed`, { method: "POST" });

/** GET /api/notes/resurface — one note untouched for a month, stable all day. */
export const getNoteResurface = () => request<NoteResurface>("/notes/resurface");

/** POST /api/notes/{id}/to-goal — the note stays; it gains a goal link. */
export const noteToGoal = (id: string, body: NoteToGoal = {}) =>
  request<Note>(`/notes/${id}/to-goal`, { method: "POST", body: JSON.stringify(body) });

/** POST /api/notes/{id}/to-task — pushes to the Todoist Inbox, records the link. */
export const noteToTask = (id: string, body: NoteToTask = {}) =>
  request<Note>(`/notes/${id}/to-task`, { method: "POST", body: JSON.stringify(body) });

/** GET /api/notebooks — the shelf, in shelf order. */
export const getNotebooks = () => request<Notebook[]>("/notebooks");

/** GET /api/notebooks/{id} — the notebook and every page in it. */
export const getNotebook = (id: string) => request<NotebookDetail>(`/notebooks/${id}`);

/** POST /api/notebooks — a new notebook, already holding its first blank page. */
export const createNotebook = (body: NotebookCreate) =>
  request<Notebook>("/notebooks", { method: "POST", body: JSON.stringify(body) });

/** PATCH /api/notebooks/{id} — only the fields sent change. */
export const updateNotebook = (id: string, body: NotebookPatch) =>
  request<Notebook>(`/notebooks/${id}`, { method: "PATCH", body: JSON.stringify(body) });

/** DELETE /api/notebooks/{id} — with every page in it. */
export const deleteNotebook = (id: string) =>
  request<void>(`/notebooks/${id}`, { method: "DELETE" });

/** POST /api/notebooks/{id}/pages — a blank page at `position`, or last. */
export const addNotebookPage = (id: string, position?: number) =>
  request<NotebookPage>(`/notebooks/${id}/pages`, {
    method: "POST",
    body: JSON.stringify(position === undefined ? {} : { position }),
  });

/** PUT /api/notebooks/pages/{id} — the autosave; a layer left out stays. */
export const saveNotebookPage = (id: string, body: NotebookPageSave) =>
  request<NotebookPage>(`/notebooks/pages/${id}`, { method: "PUT", body: JSON.stringify(body) });

/** DELETE /api/notebooks/pages/{id} — a notebook's only page is cleared instead. */
export const deleteNotebookPage = (id: string) =>
  request<void>(`/notebooks/pages/${id}`, { method: "DELETE" });

/** POST /api/finance/transactions/{id}/reimburse — this income repays that expense. */
export const markReimbursement = (id: string, body: ReimburseRequest) =>
  request<Transaction>(`/finance/transactions/${id}/reimburse`, {
    method: "POST",
    body: JSON.stringify(body),
  });

/** DELETE /api/finance/transactions/{id}/reimburse — back to ordinary income. */
export const unmarkReimbursement = (id: string) =>
  request<Transaction>(`/finance/transactions/${id}/reimburse`, { method: "DELETE" });

/**
 * GET /api/calendar/day?date=YYYY-MM-DD — one day of the primary calendar.
 *
 * «Мій день» reads today out of `/api/summary`; this is how its `‹ ›` walks to
 * any other day without pulling the whole dashboard again. Never 500s: an
 * account without Google answers `available: false` and a note.
 */
export const getCalendarDay = (date: string) =>
  request<CalendarDay>("/calendar/day", { params: { date } });

export { request as rawRequest };
